import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Eye, EyeOff, RefreshCw, Search, User, Check, ChevronLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useCustomerAccounts, type CreateCustomerAccountInput } from '@/hooks/useCustomerAccounts';
import { useCustomers, type Customer } from '@/hooks/useCustomers';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

const formSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters').max(50),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  pin: z.string().regex(/^\d{4,6}$/, 'PIN must be 4-6 digits').optional().or(z.literal('')),
  initial_balance: z.coerce.number().min(0, 'Balance cannot be negative').optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preSelectedCustomer?: Customer | null;
}

function generatePassword(length = 8): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

function generatePin(): string {
  return Math.floor(1000 + Math.random() * 9000).toString();
}

type Step = 'select-customer' | 'credentials';

export function CreateCustomerAccountDialog({ open, onOpenChange, preSelectedCustomer }: Props) {
  const { createAccount } = useCustomerAccounts();
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState<Step>('select-customer');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { customers, isLoading: customersLoading } = useCustomers(debouncedSearch);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle preSelectedCustomer - skip to credentials step
  useEffect(() => {
    if (open && preSelectedCustomer) {
      setSelectedCustomer(preSelectedCustomer);
      // Auto-generate username from customer name
      const suggestedUsername = `${preSelectedCustomer.first_name?.toLowerCase() || ''}${preSelectedCustomer.last_name?.toLowerCase() || ''}`.replace(/\s+/g, '') || 'customer';
      setValue('username', suggestedUsername);
      setStep('credentials');
    }
  }, [open, preSelectedCustomer]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setStep('select-customer');
      setSelectedCustomer(null);
      setSearchQuery('');
      setDebouncedSearch('');
      reset();
    }
  }, [open]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
      pin: '',
      initial_balance: 0,
    },
  });

  const password = watch('password');
  const pin = watch('pin');

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    // Auto-generate username from customer name
    const suggestedUsername = `${customer.first_name?.toLowerCase() || ''}${customer.last_name?.toLowerCase() || ''}`.replace(/\s+/g, '') || 'customer';
    setValue('username', suggestedUsername);
    setStep('credentials');
  };

  const onSubmit = async (data: FormValues) => {
    if (!selectedCustomer) {
      toast.error('Error', {
        description: 'Please select a customer first',
      });
      return;
    }

    try {
      const input: CreateCustomerAccountInput = {
        username: data.username,
        password: data.password,
        pin: data.pin || undefined,
        customer_id: selectedCustomer.id,
        initial_balance: data.initial_balance || 0,
      };

      await createAccount.mutateAsync(input);

      const customerName = `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim();
      toast.success('Account Created', {
        description: `Customer account for ${customerName} has been created successfully.`,
      });

      reset();
      setSelectedCustomer(null);
      setStep('select-customer');
      onOpenChange(false);
    } catch (error: any) {
      const errorMessage = error?.message || '';
      let description = 'Failed to create customer account';

      // Handle specific database constraint errors
      if (errorMessage.includes('idx_customer_accounts_username_org') ||
          errorMessage.includes('duplicate key') && errorMessage.includes('username')) {
        description = 'This username is already taken. Please choose a different username.';
      } else if (errorMessage) {
        description = errorMessage;
      }

      toast.error('Error', {
        description,
      });
    }
  };

  const handleGeneratePassword = () => {
    setValue('password', generatePassword());
  };

  const handleGeneratePin = () => {
    setValue('pin', generatePin());
  };

  const handleBack = () => {
    if (preSelectedCustomer) {
      // If we came from a pre-selected customer, close the dialog
      onOpenChange(false);
    } else {
      setStep('select-customer');
    }
  };

  // Filter out customers who already have accounts
  // Note: This is a basic filter - ideally we'd have this info from the backend
  const availableCustomers = customers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {step === 'select-customer' ? 'Select Customer' : 'Create Account'}
          </DialogTitle>
          <DialogDescription>
            {step === 'select-customer'
              ? 'Search and select an existing customer to create an account for.'
              : `Set up login credentials for ${selectedCustomer?.first_name || ''} ${selectedCustomer?.last_name || ''}`}
          </DialogDescription>
        </DialogHeader>

        {step === 'select-customer' ? (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search customers by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Customer List */}
            <div className="max-h-[300px] overflow-y-auto border rounded-lg">
              {customersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : availableCustomers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center px-4">
                  <User className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery ? 'No customers found' : 'No customers yet'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {searchQuery ? 'Try a different search term' : 'Create a customer first in the Customers tab'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {availableCustomers.map((customer) => {
                    const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed';
                    const isSelected = selectedCustomer?.id === customer.id;

                    return (
                      <button
                        key={customer.id}
                        type="button"
                        onClick={() => handleSelectCustomer(customer)}
                        className={cn(
                          "w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors",
                          isSelected && "bg-primary/10"
                        )}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                            {customer.first_name?.[0] || '?'}
                            {customer.last_name?.[0] || ''}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {customer.email && <span className="truncate">{customer.email}</span>}
                            {customer.email && customer.phone && <span>|</span>}
                            {customer.phone && <span>{customer.phone}</span>}
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="h-5 w-5 text-primary shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Selected Customer Info */}
            {selectedCustomer && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                    {selectedCustomer.first_name?.[0] || '?'}
                    {selectedCustomer.last_name?.[0] || ''}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">
                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {selectedCustomer.email || selectedCustomer.phone || 'No contact info'}
                  </p>
                </div>
                {!preSelectedCustomer && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleBack}
                  >
                    Change
                  </Button>
                )}
              </div>
            )}

            {/* Login Credentials */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium">Login Credentials</h4>

              <div className="space-y-2">
                <Label htmlFor="username">Username *</Label>
                <Input
                  id="username"
                  {...register('username')}
                  placeholder="johndoe"
                />
                {errors.username && (
                  <p className="text-xs text-destructive">{errors.username.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      {...register('password')}
                      placeholder="Min 6 characters"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleGeneratePassword}
                    title="Generate password"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive">{errors.password.message}</p>
                )}
                {password && (
                  <p className="text-xs text-muted-foreground">
                    Share this password with the customer securely.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="pin">PIN (Optional - for quick login)</Label>
                <div className="flex gap-2">
                  <Input
                    id="pin"
                    {...register('pin')}
                    placeholder="4-6 digit PIN"
                    maxLength={6}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleGeneratePin}
                    title="Generate PIN"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                {errors.pin && (
                  <p className="text-xs text-destructive">{errors.pin.message}</p>
                )}
                {pin && (
                  <p className="text-xs text-muted-foreground">
                    Customer can use their ID number + this PIN to login quickly.
                  </p>
                )}
              </div>
            </div>

            {/* Initial Balance */}
            <div className="pt-4 border-t space-y-2">
              <Label htmlFor="initial_balance">Initial Wallet Balance</Label>
              <Input
                id="initial_balance"
                type="number"
                step="0.01"
                min="0"
                {...register('initial_balance')}
                placeholder="0.00"
              />
              {errors.initial_balance && (
                <p className="text-xs text-destructive">{errors.initial_balance.message}</p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <Button type="submit" variant="blaze" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
